import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useAtom } from 'jotai';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Box,
  FormControl,
  OutlinedInput,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import { Toaster } from 'react-hot-toast';
import LocationComponent from '../../stateless/Location';
import { useEditStore } from '../../Query/ManageStoreQuery';
import { storeAtom } from './ManageRestaurantTable';
import SelectStore from '../Store/AddStore';
import ImageUploadField from '../Utils/ImageUploadField';
import { Formik, Form, Field } from 'formik';
import MerchantNameField from './MerchantNameField';
import CategoryIdField from './CategoryIdField';
import StorePhoneNumberField from './StorePhoneNumberField';
import MyEditor from '../../stateless/RichTextEditor';
import { uploadImageWithPresignedUrl } from '../../HelperFunction/s3Service';
import { resizeImage } from '../Utils/resizeImage';
import { useContext } from 'react';
import { HimmaContext } from '../../../Context/HimmaContext';
import { splitPhoneNumber } from '../../../utils/heplingFuctions';

// interface MerchantData {
//   id: string;
//   name: string;
// }

// interface CategoryData {
//   id: string;
//   nameEnglish: string;
// }

// interface Option {
//   value: string;
//   label: string;
// }

const validationSchema = (t: any) => Yup.object().shape({
  merchantId: Yup.string().required(t('storeManagement.form.validation.requiredMerchant')),
  categoryId: Yup.array().of(Yup.string()).min(1, t('storeManagement.form.validation.requiredCategory')),
   storeName: Yup.string()
    .required(t('storeManagement.form.validation.requiredStoreName'))
    .max(50, t('storeManagement.form.validation.storeNameMaxLength'))
    .matches(
      /^[^0-9]+$/,
      t('storeManagement.form.validation.storeNameNoNumbers')
    )
    .test(
      'no-leading-trailing-spaces',
      t('storeManagement.form.validation.storeNameNoSpaces'),
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      t('storeManagement.form.validation.storeNameNotOnlySpaces'),
      (value) => value?.trim().length > 0
    )
    .test(
      'not-start-with-special',
      t('storeManagement.form.validation.storeNameNoSpecialStart'),
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      t('storeManagement.form.validation.storeNameNotOnlySpecial'),
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one letter or space (not just special chars)
        return /[a-zA-Z\s]/.test(trimmed);
      }
    ),
  storeNameArabic: Yup.string()
    .required(t('storeManagement.form.validation.requiredStoreNameArabic'))
    .max(50, t('storeManagement.form.validation.storeNameArabicMaxLength'))
    .test(
      'no-leading-trailing-spaces',
      t('storeManagement.form.validation.storeNameArabicNoSpaces'),
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      t('storeManagement.form.validation.storeNameArabicNotOnlySpaces'),
      (value) => value?.trim().length > 0
    )
    .matches(
      /^[\u0600-\u06FF\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/,
      t('storeManagement.form.validation.storeNameArabicOnlyArabic')
    )
    .matches(
      /^[^0-9]+$/,
      t('storeManagement.form.validation.storeNameArabicNoNumbers')
    )
    .test(
      'not-start-with-special',
      t('storeManagement.form.validation.storeNameArabicNoSpecialStart'),
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      t('storeManagement.form.validation.storeNameArabicNotOnlySpecial'),
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one Arabic letter or space (not just special chars)
        return /[\u0600-\u06FF\s]/.test(trimmed);
      }
    ),
  minimumOrderAmount: Yup.number()
    .typeError(t('storeManagement.form.validation.minimumOrderAmountNumber'))
    .min(0, t('storeManagement.form.validation.minimumOrderAmountMin'))
    .required(t('storeManagement.form.validation.requiredMinimumOrderAmount')),
  canPreOrder: Yup.string().required(t('storeManagement.form.validation.requiredPreOrderOption')),
  // storeType: Yup.string().required('Please select a Store Type'),
  // restaurantType: Yup.array()
  //   .of(Yup.string())
  //   .min(1, 'Please select at least one restaurant type'),
  // preferableTime: Yup.array()
  //   .of(Yup.string())
  //   .min(1, 'Please select at least one preferable time'),
  deliveryTime: Yup.string()
    .matches(/^[0-9]+$/, t('storeManagement.form.validation.deliveryTimeNumeric'))
    .required(t('storeManagement.form.validation.requiredDeliveryTime')),
  additionalCulinaryRequest: Yup.string().required(
    t('storeManagement.form.validation.requiredAdditionalCulinaryRequest')
  ),
  aboutStore: Yup.string()
    .test(
      'has-content',
      t('storeManagement.form.validation.requiredAboutStore'),
      value => {
        if (!value) return false;
        // Remove HTML tags and whitespace
        const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        // Check for at least one letter or number
        return /[a-zA-Z0-9]/.test(text);
      }
    )
    .required(t('storeManagement.form.validation.requiredAboutStore')),
  aboutStoreArabic: Yup.string()
  .test(
    'has-arabic-content',
    t('storeManagement.form.validation.requiredAboutStoreArabic'),
    value => {
      if (!value) return false;
      const text = value
        .replace(/<[^>]*>/g, '')      // Remove HTML tags
        .replace(/&nbsp;/g, '')       // Remove &nbsp;
        .trim();

      // Check for at least one Arabic character and only Arabic-compatible content
      const arabicRegex = /^[\u0600-\u06FF\s0-9،؛؟.,\-!()]+$/; // Includes Arabic letters, digits, common punctuation
      return arabicRegex.test(text);
    }
  )
  .required(t('storeManagement.form.validation.requiredAboutStoreArabic')),
  deliveryRadiusKM: Yup.string()
    .matches(/^[0-9]+$/, t('storeManagement.form.validation.deliveryRadiusNumeric'))
    .required(t('storeManagement.form.validation.requiredDeliveryRadius')),
  storePhoneNumber: Yup.string()
    .required(t('storeManagement.form.validation.requiredStorePhoneNumber'))
    .matches(/^[0-9]+$/, t('storeManagement.form.validation.storePhoneNumberNumeric'))
    .test(
      'len',
      t('storeManagement.form.validation.storePhoneNumberLength'),
      (val) => val?.length >= 9
    ),
  storeLogo: Yup.mixed()
    // .required('Restaurant logo is required')
    .test('fileSize', t('storeManagement.form.validation.logoSize'), (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      t('storeManagement.form.validation.logoDimensions'),
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    ),

  storeBanner: Yup.array()
    .min(1, 'At least one banner image is required')
    .max(6, 'Maximum 6 banner images allowed')
    .test(
      'fileSize',
      'Each banner must be less than 1MB',
      (value: any[] | undefined) => {
        if (!value || !Array.isArray(value)) return true;
        return value.every((file) => {
          if (typeof file === 'string') return true;
          return file.size <= 1 * 1024 * 1024;
        });
      }
    )
    .test(
      'dimensions',
      'Image dimensions must be between 1366×300 and 1500×500',
      async (value: any[] | undefined): Promise<boolean> => {
        if (!value || !Array.isArray(value)) return true;

        const dimensionChecks = await Promise.all(
          value.map(async (file) => {
            if (typeof file === 'string') return { isValid: true };
            if (!(file instanceof File)) return { isValid: true };

            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                  const { width, height } = img;
                  const isValid =
                    width >= 1366 &&
                    width <= 1500 &&
                    height >= 300 &&
                    height <= 500;
                  resolve({ isValid, width, height });
                };
                img.onerror = () => resolve({ isValid: false });
                img.src = event.target?.result as string;
              };
              reader.onerror = () => resolve({ isValid: false });
              try {
                reader.readAsDataURL(file);
              } catch (error) {
                console.error('Error reading file:', error);
                resolve({ isValid: false });
              }
            });
          })
        );

        const invalidDimensions = dimensionChecks
          .filter((check: any) => !check.isValid && check.width && check.height)
          .map((check: any) => `${check.width}×${check.height}`);

        if (invalidDimensions.length > 0) {
          return false;
        }

        return true;
      }
    ),
  workingHours: Yup.object({
    sunday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    monday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    tuesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    wednesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    thursday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    friday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    saturday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
  }),

  itemAvailableTime: Yup.object()
    .shape({
      breakfast: Yup.object().shape({
        startTime: Yup.string()
          .test('breakfast-start-required', 'Start time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Breakfast')) {
              return !!value;
            }
            return true;
          }),
        endTime: Yup.string()
          .test('breakfast-end-required', 'End time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Breakfast')) {
              if (!value) return false;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
            return true;
          }),
      }),
      lunch: Yup.object().shape({
        startTime: Yup.string()
          .test('lunch-start-required', 'Start time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Lunch')) {
              return !!value;
            }
            return true;
          }),
        endTime: Yup.string()
          .test('lunch-end-required', 'End time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Lunch')) {
              if (!value) return false;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
            return true;
          }),
      }),
      dinner: Yup.object().shape({
        startTime: Yup.string()
          .test('dinner-start-required', 'Start time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Dinner')) {
              return !!value;
            }
            return true;
          }),
        endTime: Yup.string()
          .test('dinner-end-required', 'End time is required', function (value) {
            const rootValues = (this.options as any)?.parent || {};
            const preferableTime = rootValues.preferableTime || [];
            if (Array.isArray(preferableTime) && preferableTime.includes('Dinner')) {
              if (!value) return false;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
            return true;
          }),
      }),
    })
    .test(
      'no-overlap',
      'Meal time ranges should not overlap',
      function (value) {
        if (!value) return true;

        // Get preferableTime from root form values
        // In Yup test functions, we can access root values through this.options.parent
        const rootValues = (this.options as any)?.parent || (this as any).options?.parent || {};
        const preferableTime = rootValues.preferableTime || [];
        const mealTimeMapping: Record<string, string> = {
          'Breakfast': 'breakfast',
          'Lunch': 'lunch',
          'Dinner': 'dinner',
        };
        const selectedMeals = Array.isArray(preferableTime)
          ? preferableTime.map((time: string) => mealTimeMapping[time]).filter(Boolean)
          : [];

        const ranges = [];
        for (const [meal, times] of Object.entries(value)) {
          // Only validate meals that are selected in preferableTime
          if (!selectedMeals.includes(meal)) continue;
          
          const start = timeToNumber(times?.startTime);
          const end = timeToNumber(times?.endTime);
          if (start === -1 || end === -1) continue;

          for (const existing of ranges) {
            if (start < existing.end && end > existing.start) {
              return this.createError({
                path: `itemAvailableTime.${meal}`,
                message: `${meal} time overlaps with ${existing.name}`,
              });
            }
          }
          ranges.push({ name: meal, start, end });
        }

        return true;
      }
    )
    .test(
      'sequential-order',
      'Each meal time must start at or after the previous meal ends (Breakfast → Lunch → Dinner)',
      function (value) {
        if (!value) return true;
        
        // Get preferableTime from root form values
        // In Yup test functions, we can access root values through this.options.parent
        const rootValues = (this.options as any)?.parent || (this as any).options?.parent || {};
        const preferableTime = rootValues.preferableTime || [];
        const mealTimeMapping: Record<string, string> = {
          'Breakfast': 'breakfast',
          'Lunch': 'lunch',
          'Dinner': 'dinner',
        };
        const selectedMeals = Array.isArray(preferableTime)
          ? preferableTime.map((time: string) => mealTimeMapping[time]).filter(Boolean)
          : [];

        const order = ['breakfast', 'lunch', 'dinner'] as const;
        type Meal = typeof order[number];
        for (let i = 1; i < order.length; i++) {
          const prevMeal = order[i - 1] as Meal;
          const currMeal = order[i] as Meal;
          
          // Only validate if both meals are selected
          if (!selectedMeals.includes(prevMeal) || !selectedMeals.includes(currMeal)) continue;
          
          const prev = value[prevMeal];
          const curr = value[currMeal];
          if (
            prev && curr &&
            prev.endTime && curr.startTime &&
            timeToNumber(curr.startTime) < timeToNumber(prev.endTime)
          ) {
            return this.createError({
              path: `itemAvailableTime.${currMeal}.startTime`,
              message: `${currMeal[0].toUpperCase() + currMeal.slice(1)} must start at or after ${prevMeal[0].toUpperCase() + prevMeal.slice(1)} ends`,
            });
          }
        }
        return true;
      }
    ),
  deliveryTimeUnit: Yup.string().required('Please select time unit'),
  storeAddress: Yup.string().required('Please enter store address'),
  latitude: Yup.string().required('Latitude is required'),
  longitude: Yup.string().required('Longitude is required'),
  deliveryFee: Yup.object().shape({
    deliveryChargeSetting: Yup.object().shape({
      peakHourDeliveryFeeStatus: Yup.object().shape({
        type: Yup.string().required(),
        data: Yup.object().when('type', {
          is: 'enable',
          then: () =>
            Yup.object().test(
              'peak-hours-validation',
              'Peak hour times are invalid',
              function (
                value:
                  | Record<
                    string,
                    { startTime: string; endTime: string; amount: number }[]
                  >
                  | undefined
              ) {
                if (!value) return true;
                const days = [
                  'sunday',
                  'monday',
                  'tuesday',
                  'wednesday',
                  'thursday',
                  'friday',
                  'saturday',
                ];
                let hasError = false;

                for (const day of days) {
                  const hours = value[day];
                  if (hours && hours.length > 0) {
                    for (const hour of hours) {
                      const isStartZero = !hour.startTime || hour.startTime === '00:00:00Z';
                      const isEndZero = !hour.endTime || hour.endTime === '00:00:00Z';
                      if (isStartZero && isEndZero) {
                        hasError = true;
                        break;
                      }
                    }
                  }
                  if (hasError) break;
                }

                if (hasError) {
                  return this.createError({
                    message:
                      'Please select a valid start and end time for all enabled peak hours.',
                  });
                }

                return true;
              }
            ),
          otherwise: () => Yup.object().notRequired(),
        }),
      }),
    }),
  }),
});

const timeToNumber = (time: string | undefined): number => {
  if (!time) return -1; // invalid or unset times return -1
  // Handle both :00Z and :59Z formats
  const cleanTime = time.replace(':00Z', '').replace(':59Z', ':59').replace(':', '');
  return parseInt(cleanTime);
};

const generateTimeOptions = () => {
  const times = [];
  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 30) {
      const hour = hours.toString().padStart(2, '0');
      const minute = minutes.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  // Add 23:59 as a special case
  times.push('23:59');
  return times;
};

// type AddRestaurantProps = {
//   onCancel: () => void;
//   storeType: string;
//   storeTypeOnEdit: ("GroceryStore" | "PharmacyStore" | "Restaurant")[];
// };

const EditRestaurant = ({ onCancel, storeType }: { onCancel: () => void; storeType: string }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const { warning } = useThemeColors();
  const [storeId] = useAtom(storeAtom);
  const apiClient = useContext(HimmaContext);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  // const countryCodes = [
  //   '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'
  // ];
  const fullNumber = storeId?.restaurantDetails?.storePhoneNumber || '';
  // const matchedCode = countryCodes.find(code => fullNumber.startsWith(code));
  // const phoneNumber = matchedCode ? fullNumber.slice(matchedCode.length) : '';
    const {countryCode:matchedCode, phone:phoneNumber} = splitPhoneNumber(fullNumber)
  const [selectedCountryCode, setSelectedCountryCode] = useState(matchedCode || '+962');
  const { mutate: EditStore } = useEditStore();
  // const { data: categoryData } = useAllCategory({
  //   initialData: {
  //     storeType: storeType,
  //   },
  // });
  // const categoryOptions = categoryData?.map((item) => ({
  //   value: item.id,
  //   label: item.nameEnglish,
  // })) || [];
  const categoryIds = storeId?.restaurantDetails?.categoryId || [];

  const initialValues = {
    categoryId: storeId ? (Array.isArray(categoryIds) ? categoryIds : [categoryIds]) : [],
    categoryName: storeId ? storeId?.restaurantCategory : '',
    storeAddress: storeId ? storeId?.restaurantDetails?.storeAddress?.address : '',
    storeName: storeId ? storeId.nameEnglish : '',
    merchantId: storeId ? storeId.restaurantDetails.merchantId : '',
    merchantName: storeId ? storeId.merchantName : '',
    storeNameArabic: storeId ? storeId.nameArabic : '',
    minimumOrderAmount: storeId ? storeId.restaurantDetails.minOrderAmount.value : null,
    canPreOrder: storeId ? (storeId.restaurantDetails.canPreOrder ? 'Yes' : 'No') : '',
    IsHalal: storeId ? (storeId.restaurantDetails.isHalal ? 'Yes' : 'No') : '',
    restaurantType: storeId ? storeId.restaurantDetails.restaurantType.map((item: string) => item.toLowerCase()) : [],
    preferableTime: storeId ? storeId.restaurantDetails.preferableTime.map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()) : [],
    hasSelfPickup: storeId ? (storeId.restaurantDetails.hasSelfPickup ? 'Yes' : 'No') : '',
    deliveryAvailable: storeId ? (storeId.restaurantDetails.deliveryAvailable ? 'Yes' : 'No') : '',
    deliveryTime: storeId ? storeId.restaurantDetails.deliveryTime.value : '',
    deliveryTimeUnit: storeId && storeId.restaurantDetails.deliveryTime.format? (storeId.restaurantDetails.deliveryTime.format.charAt(0).toUpperCase() + storeId.restaurantDetails.deliveryTime.format.slice(1)) : '',
    additionalCulinaryRequest: storeId ? (storeId.restaurantDetails.additionalCulinaryRequest ? 'Yes' : 'No') : '',
    aboutStore: storeId ? storeId.restaurantDetails.aboutStore : '',
    aboutStoreArabic: storeId ? storeId.restaurantDetails.aboutStoreArabic : '',
    deliveryRadiusKM: storeId ? storeId.restaurantDetails.deliveryRadiusKM : '',
    storePhoneNumber: phoneNumber,
    storeLogo: storeId ? storeId.restaurantDetails.storeLogo : null,
    storeBanner: storeId ? storeId.restaurantDetails.storeBanner : [],
    latitude: storeId ? storeId.restaurantDetails?.storeAddress?.latitude : '',
    longitude: storeId ? storeId.restaurantDetails?.storeAddress?.longitude : '',
    location: storeId ? {
      address: storeId?.restaurantDetails?.storeAddress.address,
      latitude: storeId?.restaurantDetails?.storeAddress.latitude,
      longitude: storeId?.restaurantDetails?.storeAddress.longitude,
    } : {
      address: '',
      latitude: '',
      longitude: '',
    },
    deliveryFee: storeId?.restaurantDetails?.deliveryFee || {
      deliveryChargeSetting: {
        type: 'default',
        deliveryFeeStatus: {
          type: 'disable',
          commonDeliveryFee: { type: 'disable', commonDeliveryFeeValue: '' },
          feePerKM: { type: 'disable', feePerKMValue: '' },
        },
        peakHourDeliveryFeeStatus: {
          type: 'disable',
          data: {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
          },
        },
      },
    },
    itemAvailableTime: {
      breakfast: storeId?.restaurantDetails?.itemAvailableTime?.breakfast || { startTime: '', endTime: '' },
      lunch: storeId?.restaurantDetails?.itemAvailableTime?.lunch || { startTime: '', endTime: '' },
      dinner: storeId?.restaurantDetails?.itemAvailableTime?.dinner || { startTime: '', endTime: '' },
    },
    workingHours: {
      sunday: storeId?.restaurantDetails?.workingHours?.sunday || { isOpen: true, startTime: '', endTime: '' },
      monday: storeId?.restaurantDetails?.workingHours?.monday || { isOpen: true, startTime: '', endTime: '' },
      tuesday: storeId?.restaurantDetails?.workingHours?.tuesday || { isOpen: true, startTime: '', endTime: '' },
      wednesday: storeId?.restaurantDetails?.workingHours?.wednesday || { isOpen: true, startTime: '', endTime: '' },
      thursday: storeId?.restaurantDetails?.workingHours?.thursday || { isOpen: true, startTime: '', endTime: '' },
      friday: storeId?.restaurantDetails?.workingHours?.friday || { isOpen: true, startTime: '', endTime: '' },
      saturday: storeId?.restaurantDetails?.workingHours?.saturday || { isOpen: true, startTime: '', endTime: '' },
    },
  };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      if (!apiClient) {
        throw new Error('API client not available');
      }

      // Handle logo upload
      let logoUrl = values.storeLogo;
      if (values.storeLogo instanceof File) {
        const resizedLogo = await resizeImage(values.storeLogo, "store logo");
        const { url } = await uploadImageWithPresignedUrl(resizedLogo, apiClient);
        logoUrl = url;
      }

      // Handle banner uploads
      let bannerUrls = values.storeBanner;
      if (values.storeBanner && values.storeBanner.length > 0) {
        const filesToUpload = values.storeBanner.filter((file: File | string) => file instanceof File) as File[];
        if (filesToUpload.length > 0) {
          const resizedBanners = await Promise.all(
            filesToUpload.map((file: File) => resizeImage(file, "store banner"))
          );
          const uploadedBanners = await Promise.all(
            resizedBanners.map((file: File) => uploadImageWithPresignedUrl(file, apiClient))
          );
          const uploadedUrls = uploadedBanners.map((result: { url: string }) => result.url);
          
          // Replace File objects with URLs, keep existing URLs
          bannerUrls = values.storeBanner.map((item: File | string) => 
            item instanceof File ? uploadedUrls.shift() || item : item
          );
        }
      }

      const formattedData = {
        ...values,
        storePhoneNumber: `${selectedCountryCode}${values.storePhoneNumber}`,
        storeName: values.storeName,
        storeNameArabic: values.storeNameArabic,
        minOrderAmount: {
          currency: 'JOD',
          value: +values.minimumOrderAmount,
        },
        canPreOrder: values.canPreOrder === 'Yes',
        isHalal: values.IsHalal === 'Yes',
        restaurantType: values.restaurantType,
        preferableTime: values.preferableTime.map((item: string) => item.toLowerCase()),
        storeType: storeType,
        hasSelfPickup: values.hasSelfPickup === 'Yes',
        deliveryAvailable: values.deliveryAvailable === 'Yes',
        deliveryTime: {
          value: +values.deliveryTime,
          format: values?.deliveryTimeUnit?.toLowerCase(),
        },
        additionalCulinaryRequest: values.additionalCulinaryRequest === 'Yes',
        aboutStore: values.aboutStore,
        aboutStoreArabic: values.aboutStoreArabic,
        storeAddress: {
          address: values.storeAddress || '',
          latitude: values.latitude || '',
          longitude: values.longitude || '',
        },
        workingHours: values.workingHours,
        itemAvailableTime: {
          breakfast: values.itemAvailableTime.breakfast,
          lunch: values.itemAvailableTime.lunch,
          dinner: values.itemAvailableTime.dinner,
        },
        deliveryRadiusKM: values.deliveryRadiusKM,
        storeLogo: logoUrl,
        storeBanner: bannerUrls,
      };
      EditStore(
        {
          id: storeId.id,
          data: formattedData,
        },
        {
          onSuccess: () => {
            onCancel();
          },
          onError: (error) => {
            console.error('EditStore error:', error);
          },
        }
      );
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const logAndValidate = async (values: typeof initialValues) => {
    try {
      // Use Yup's validation schema to validate
      await validationSchema(t).validate(values, { abortEarly: false });
      return {};
    } catch (err: any) {
      if (err && err.inner) {
        const errorMap: Record<string, string> = {};
        err.inner.forEach((validationError: any) => {
          if (validationError.path && !errorMap[validationError.path]) {
            errorMap[validationError.path] = validationError.message;
          }
        });
        // Log all errors to the console
        console.error('Formik Validation Errors:', errorMap);
        return errorMap;
      }
      // Log any other error
      console.error('Formik Validation Error:', err);
      return {};
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <SelectStore
        onCancel={onCancel}
        storeTypeOnEdit={[storeId?.storeType || storeType]}
      />
      <Box
        maxWidth={800}
        mx="auto"
        my={4}
        p={{ xs: 2, sm: 4 }}
        bgcolor="#fff"
        borderRadius={3}
        boxShadow={2}
       >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          enableReinitialize
          onSubmit={handleSubmit}
          validate={logAndValidate}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched, submitCount }) => {
            const shouldShowItemAvailableTime =
              Array.isArray(values.preferableTime) &&
              values.preferableTime.length > 0 &&
              values.preferableTime.some((time: string) => 
                ['Breakfast', 'Lunch', 'Dinner'].includes(time)
              );

            // Map preferableTime values to meal keys (Breakfast -> breakfast, etc.)
            const mealTimeMapping: Record<string, 'breakfast' | 'lunch' | 'dinner'> = {
              'Breakfast': 'breakfast',
              'Lunch': 'lunch',
              'Dinner': 'dinner',
            };

            // Get only the meal keys that are selected in preferableTime
            const selectedMealKeys = Array.isArray(values.preferableTime)
              ? values.preferableTime
                  .map((time: string) => mealTimeMapping[time])
                  .filter((key): key is 'breakfast' | 'lunch' | 'dinner' => key !== undefined)
              : [];

            // console.log('Formik values:', values);
            // console.log('Formik errors:', errors);
            return (
              <Form>
              {/* Merchant Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.merchantName')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="merchantName" component={MerchantNameField} storeId={storeId} />
                </Box>
              </Box>
              {/* Category */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.category')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="categoryId" component={CategoryIdField} storeId={storeId} />
                </Box>
              </Box>
              {/* Store Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeName')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeName && Boolean(errors.storeName)} fullWidth>
                    <OutlinedInput
                      name="storeName"
                      value={values.storeName}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 50) {
                          setFieldValue('storeName', newValue);
                          setFieldTouched('storeName', true);
                        } else {
                          setFieldValue('storeName', newValue.slice(0, 50));
                        }
                      }}
                      onBlur={() => setFieldTouched('storeName', true)}
                      placeholder="Enter Restaurant Name"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeName && typeof errors.storeName === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeName}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Name Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeNameArabic')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeNameArabic && Boolean(errors.storeNameArabic)} fullWidth>
                    <OutlinedInput
                      name="storeNameArabic"
                      value={values.storeNameArabic}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 50) {
                          setFieldValue('storeNameArabic', newValue);
                          setFieldTouched('storeNameArabic', true);
                        } else {
                          setFieldValue('storeNameArabic', newValue.slice(0, 50));
                        }
                      }}
                      onBlur={() => setFieldTouched('storeNameArabic', true)}
                      placeholder="Enter Restaurant Name in Arabic"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeNameArabic && typeof errors.storeNameArabic === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeNameArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Minimum Order Amount */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.minimumOrderAmount')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.minimumOrderAmount && Boolean(errors.minimumOrderAmount)} fullWidth>
                    <Box sx={{ display: 'flex', gap: '4px', width: { xs: '100%', sm: '400px' } }}>
                      <Box sx={{ width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>JOD</Box>
                      <OutlinedInput
                        type="number"
                        value={values.minimumOrderAmount}
                        onChange={(e) => {
                          setFieldValue('minimumOrderAmount', e.target.value);
                          setFieldTouched('minimumOrderAmount', true, false);
                        }}
                        placeholder="Enter Minimum Order Amount"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                  {touched.minimumOrderAmount && typeof errors.minimumOrderAmount === 'string' && (
                    <Typography variant="caption" color="error">{errors.minimumOrderAmount}</Typography>
                  )}
                </Box>
              </Box>
              {/* Pre-order Option */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.preOrderOption')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.canPreOrder && Boolean(errors.canPreOrder)} fullWidth>
                    <RadioGroup
                      row
                      name="canPreOrder"
                      value={values.canPreOrder}
                      onChange={(e) => setFieldValue('canPreOrder', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.canPreOrder && typeof errors.canPreOrder === 'string' && (
                      <Typography variant="caption" color="error">{errors.canPreOrder}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Halal */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.halal')} </Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.IsHalal && Boolean(errors.IsHalal)} fullWidth>
                    <RadioGroup
                      row
                      name="IsHalal"
                      value={values.IsHalal}
                      onChange={(e) => setFieldValue('IsHalal', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.IsHalal && typeof errors.IsHalal === 'string' && (
                      <Typography variant="caption" color="error">{errors.IsHalal}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Type of Restaurant */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.typeOfRestaurant')} </Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.restaurantType && Boolean(errors.restaurantType)} fullWidth>
                    <Box sx={{ display: 'flex', gap: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.restaurantType.includes('veg')}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newArr = [...values.restaurantType];
                              if (checked) {
                                newArr.push('veg');
                              } else {
                                newArr = newArr.filter((v: string) => v !== 'veg');
                              }
                              setFieldValue('restaurantType', newArr);
                              setFieldTouched('restaurantType', true);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{t('storeManagement.form.veg')}</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.restaurantType.includes('non-veg')}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newArr = [...values.restaurantType];
                              if (checked) {
                                newArr.push('non-veg');
                              } else {
                                newArr = newArr.filter((v: string) => v !== 'non-veg');
                              }
                              setFieldValue('restaurantType', newArr);
                              setFieldTouched('restaurantType', true);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{t('storeManagement.form.nonVeg')}</Typography>}
                      />
                    </Box>
                  </FormControl>
                  {touched.restaurantType && typeof errors.restaurantType === 'string' && (
                    <Typography variant="caption" color="error">{errors.restaurantType}</Typography>
                  )}
                </Box>
              </Box>
              {/* Preferable Time */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.preferableTime')} </Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.preferableTime && Boolean(errors.preferableTime)} fullWidth>
                    <Box sx={{ display: 'flex', gap: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.preferableTime.includes('Breakfast')}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newArr = [...values.preferableTime];
                              if (checked) {
                                newArr.push('Breakfast');
                              } else {
                                newArr = newArr.filter((v: string) => v !== 'Breakfast');
                              }
                              setFieldValue('preferableTime', newArr);
                              setFieldTouched('preferableTime', true);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{t('storeManagement.form.breakfast')}</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.preferableTime.includes('Lunch')}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newArr = [...values.preferableTime];
                              if (checked) {
                                newArr.push('Lunch');
                              } else {
                                newArr = newArr.filter((v: string) => v !== 'Lunch');
                              }
                              setFieldValue('preferableTime', newArr);
                              setFieldTouched('preferableTime', true);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{t('storeManagement.form.lunch')}</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.preferableTime.includes('Dinner')}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              let newArr = [...values.preferableTime];
                              if (checked) {
                                newArr.push('Dinner');
                              } else {
                                newArr = newArr.filter((v: string) => v !== 'Dinner');
                              }
                              setFieldValue('preferableTime', newArr);
                              setFieldTouched('preferableTime', true);
                            }}
                          />
                        }
                        label={<Typography variant="body2">{t('storeManagement.form.dinner')}</Typography>}
                      />
                    </Box>
                  </FormControl>
                  {touched.preferableTime && typeof errors.preferableTime === 'string' && (
                    <Typography variant="caption" color="error">{errors.preferableTime}</Typography>
                  )}
                </Box>
              </Box>
              {/* Self Pickup */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.selfPickup')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.hasSelfPickup && Boolean(errors.hasSelfPickup)} fullWidth>
                    <RadioGroup
                      row
                      name="hasSelfPickup"
                      value={values.hasSelfPickup}
                      onChange={(e) => setFieldValue('hasSelfPickup', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.hasSelfPickup && typeof errors.hasSelfPickup === 'string' && (
                      <Typography variant="caption" color="error">{errors.hasSelfPickup}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Delivery Available */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.deliveryAvailable')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryAvailable && Boolean(errors.deliveryAvailable)} fullWidth>
                    <RadioGroup
                      row
                      name="deliveryAvailable"
                      value={values.deliveryAvailable}
                      onChange={(e) => setFieldValue('deliveryAvailable', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.deliveryAvailable && typeof errors.deliveryAvailable === 'string' && (
                      <Typography variant="caption" color="error">{errors.deliveryAvailable}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Delivery Fee - hidden configuration, defaults applied internally */}
              {/* <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.deliveryFee')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <DeliveryFeeForm field={{ name: 'deliveryFee', value: values.deliveryFee }} form={{ values, setFieldValue }} errors={errors} touched={touched} />
                </Box>
              </Box> */}
              {/* Response Time and Unit (combined row) */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.responseTime')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: '400px' } }}>
                    <FormControl error={touched.deliveryTime && Boolean(errors.deliveryTime)} sx={{ flex: 1 }}>
                      <OutlinedInput
                        name="deliveryTime"
                        value={values.deliveryTime}
                        onChange={(e) => setFieldValue('deliveryTime', e.target.value)}
                        onBlur={() => setFieldTouched('deliveryTime', true)}
                        onKeyDown={(e) => {
                          const isDigit = /^[0-9]$/.test(e.key);
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                          ];
                          if (!isDigit && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder={t('storeManagement.form.placeholders.deliveryTime')}
                      />
                    </FormControl>
                    <FormControl error={touched.deliveryTimeUnit && Boolean(errors.deliveryTimeUnit)} sx={{ minWidth: 120 }}>
                      <Select
                        value={values.deliveryTimeUnit}
                        onChange={(e) => setFieldValue('deliveryTimeUnit', e.target.value)}
                        displayEmpty
                        input={<OutlinedInput />}
                      >
                        <MenuItem value="">
                          <em>Select Unit</em>
                        </MenuItem>
                        <MenuItem value="Minutes">{t('storeManagement.form.minutes')}</MenuItem>
                        <MenuItem value="Hours">Hours</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {(touched.deliveryTime && typeof errors.deliveryTime === 'string') && (
                    <Typography variant="caption" color="error">{errors.deliveryTime}</Typography>
                  )}
                  {(touched.deliveryTimeUnit && typeof errors.deliveryTimeUnit === 'string') && (
                    <Typography variant="caption" color="error">{errors.deliveryTimeUnit}</Typography>
                  )}
                </Box>
              </Box>
              {/* Additional Cutlery Request */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.additionalCulinaryRequest')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.additionalCulinaryRequest && Boolean(errors.additionalCulinaryRequest)} fullWidth>
                    <RadioGroup
                      row
                      name="additionalCulinaryRequest"
                      value={values.additionalCulinaryRequest}
                      onChange={(e) => setFieldValue('additionalCulinaryRequest', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.additionalCulinaryRequest && typeof errors.additionalCulinaryRequest === 'string' && (
                      <Typography variant="caption" color="error">{errors.additionalCulinaryRequest}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* About Store */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.aboutStore')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStore}
                    onChange={(val: string) => setFieldValue('aboutStore', val)}
                    onBlur={() => setFieldTouched('aboutStore', true)}
                  />
                  {((touched.aboutStore || submitCount > 0) && typeof errors.aboutStore === 'string') && (
                    <Typography variant="caption" color="error">{errors.aboutStore}</Typography>
                  )}
                </Box>
              </Box>
              {/* About Store Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.aboutStoreArabic')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStoreArabic}
                    onChange={(val: string) => setFieldValue('aboutStoreArabic', val)}
                    onBlur={() => setFieldTouched('aboutStoreArabic', true)}
                  />
                  {((touched.aboutStoreArabic || submitCount > 0) && typeof errors.aboutStoreArabic === 'string') && (
                    <Typography variant="caption" color="error">{errors.aboutStoreArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Delivery Radius */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.deliveryRadius')}<span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryRadiusKM && Boolean(errors.deliveryRadiusKM)} fullWidth>
                    <OutlinedInput
                      name="deliveryRadiusKM"
                      value={values.deliveryRadiusKM}
                      onChange={(e) => setFieldValue('deliveryRadiusKM', e.target.value)}
                      onBlur={() => setFieldTouched('deliveryRadiusKM', true)}
                      onKeyDown={(e) => {
                        const isDigit = /^[0-9]$/.test(e.key);
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'ArrowLeft',
                          'ArrowRight',
                        ];
                        if (!isDigit && !allowedKeys.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Enter Delivery Radius"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />

                  </FormControl>
                  {touched.deliveryRadiusKM && typeof errors.deliveryRadiusKM === 'string' && (
                    <Typography variant="caption" color="error">{errors.deliveryRadiusKM}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Phone Number */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storePhoneNumber')}<span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field
                    name="storePhoneNumber"
                    component={StorePhoneNumberField}
                    selectedCountryCode={selectedCountryCode}
                    onCountryCodeChange={setSelectedCountryCode}
                  />
                </Box>
              </Box>
              {/* Store Address */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeAddress')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Store Address</Typography> */}
                  <LocationComponent
                    field={{ name: 'storeAddress', value: values.storeAddress }}
                    form={{ values, errors, touched, setFieldValue, setFieldTouched }}
                  />
                </Box>
              </Box>
              {/* Store Logo */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeLogo')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeLogo" label={t('storeManagement.form.storeLogo')} />
                    {(touched.storeLogo || values.storeLogo) && typeof errors.storeLogo === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeLogo}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Store Banner */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.banner')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeBanner" label={t('storeManagement.form.banner')} multiple maxFiles={6} recommendedText={t('storeManagement.form.maxImages')} />
                    {(touched.storeBanner || (values.storeBanner && values.storeBanner.length > 0)) && typeof errors.storeBanner === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeBanner}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {shouldShowItemAvailableTime && (
                <>
                  {/* Item Available Time */}
                  <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                      <Typography variant="subtitle2">{t('storeManagement.form.itemAvailableTime')} <span style={{ color: 'red' }}>*</span></Typography>
                    </Box>
                    <Box flex={1} width="100%">
                      <FormControl fullWidth>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {selectedMealKeys.map((mealKey) => {
                            const mealErrors = (errors?.itemAvailableTime && (errors.itemAvailableTime as any)[mealKey]) || {};
                            const mealTouched = (touched?.itemAvailableTime && (touched.itemAvailableTime as any)[mealKey]) || {};
                            return (
                              <Box
                                key={mealKey}
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                                  <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{t(`storeManagement.form.${mealKey}`)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {/* Start Time */}
                                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }} error={Boolean(mealTouched?.startTime && mealErrors?.startTime)}>
                                    <Select
                                      value={values.itemAvailableTime[mealKey]?.startTime?.replace(':00Z', '').replace(':59Z', '') || ''}
                                      onChange={(e) => {
                                        const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : `${e.target.value}:00Z`;
                                        setFieldValue(`itemAvailableTime.${mealKey}.startTime`, timeValue);
                                        setFieldTouched(`itemAvailableTime.${mealKey}.startTime`, true, false);
                                      }}
                                      onBlur={() => setFieldTouched(`itemAvailableTime.${mealKey}.startTime`, true, false)}
                                      displayEmpty
                                    >
                                      <MenuItem value="">
                                        <em>{t('storeManagement.form.selectStart')}</em>
                                      </MenuItem>
                                      {generateTimeOptions().map((time) => (
                                        <MenuItem key={time} value={time}>{time}</MenuItem>
                                      ))}
                                    </Select>
                                    {mealTouched?.startTime && typeof mealErrors?.startTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{mealErrors.startTime}</Typography>
                                    )}
                                  </FormControl>
                                  {/* End Time */}
                                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }} error={Boolean(mealTouched?.endTime && mealErrors?.endTime)}>
                                    <Select
                                      value={values.itemAvailableTime[mealKey]?.endTime?.replace(':00Z', '').replace(':59Z', '') || ''}
                                      onChange={(e) => {
                                        const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : `${e.target.value}:00Z`;
                                        setFieldValue(`itemAvailableTime.${mealKey}.endTime`, timeValue);
                                        setFieldTouched(`itemAvailableTime.${mealKey}.endTime`, true, false);
                                      }}
                                      onBlur={() => setFieldTouched(`itemAvailableTime.${mealKey}.endTime`, true, false)}
                                      displayEmpty
                                    >
                                      <MenuItem value="">
                                        <em>{t('storeManagement.form.selectEnd')}</em>
                                      </MenuItem>
                                      {generateTimeOptions().map((time) => (
                                        <MenuItem key={time} value={time}>{time}</MenuItem>
                                      ))}
                                    </Select>
                                    {mealTouched?.endTime && typeof mealErrors?.endTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{mealErrors.endTime}</Typography>
                                    )}
                                  </FormControl>
                                </Box>
                              </Box>
                            );
                          })}
                          {/* Overlap error */}
                          {touched?.itemAvailableTime && typeof errors?.itemAvailableTime === 'string' && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.itemAvailableTime}</Typography>
                          )}
                        </Box>
                      </FormControl>
                    </Box>
                  </Box>
                </>
              )}
              {/* Working Hours */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.workingHours')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl fullWidth>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      {(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const).map((dayKey) => {
                        const dayData = values.workingHours[dayKey];
                        const dayErrors = (errors?.workingHours && (errors.workingHours as any)[dayKey]) || {};
                        const dayTouched = (touched?.workingHours && (touched.workingHours as any)[dayKey]) || {};
                        return (
                          <Box key={dayKey} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{t(`storeManagement.form.${dayKey}`)} *</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!dayData.isOpen}
                                    onChange={(e) => {
                                      setFieldValue(`workingHours.${dayKey}.isOpen`, !e.target.checked);
                                      if (e.target.checked) {
                                        setFieldValue(`workingHours.${dayKey}.startTime`, '00:00:00Z');
                                        setFieldValue(`workingHours.${dayKey}.endTime`, '00:00:00Z');
                                      }
                                      setFieldTouched(`workingHours.${dayKey}`, true, false);
                                    }}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2" color="text.secondary">{t('storeManagement.form.closed')}</Typography>}
                              />
                              {dayData.isOpen && (
                                <Box sx={{ display: 'flex', gap: 4 }}>
                                  {/* Start Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.startTime && dayErrors?.startTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.startTime ? dayData.startTime.replace(':00Z', '').replace(':59Z', '') : ''}
                                        onChange={(e) => {
                                          const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : (e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldValue(`workingHours.${dayKey}.startTime`, timeValue);
                                          setFieldTouched(`workingHours.${dayKey}.startTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.startTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>{t('storeManagement.form.selectStart')}</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.startTime && typeof dayErrors?.startTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.startTime}</Typography>
                                    )}
                                  </Box>
                                  {/* End Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.endTime && dayErrors?.endTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.endTime ? dayData.endTime.replace(':00Z', '').replace(':59Z', '') : ''}
                                        onChange={(e) => {
                                          const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : (e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldValue(`workingHours.${dayKey}.endTime`, timeValue);
                                          setFieldTouched(`workingHours.${dayKey}.endTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.endTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>{t('storeManagement.form.selectEnd')}</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.endTime && typeof dayErrors?.endTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.endTime}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </FormControl>
                </Box>
              </Box>
              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center', width: '100%', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', fontSize: 14, px: 3, py: 1, backgroundColor: COLORS.primary.main, '&:hover': { backgroundColor: 'rgba(76, 10, 39, 0.8)' }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (<><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />{t('storeItem.form.updating')}</>) : (t('common.update') || 'Update')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={onCancel}
                  sx={{ bgcolor: warning.main, color: '#fff', '&:hover': { bgcolor: warning.dark, color: '#fff' } }}
                >
                  {t('storeItem.form.cancel')}
                </Button>
              </Box>
            </Form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default EditRestaurant;
